import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api/client';
import { useToggles } from '../context/TogglesContext';
import { useSidebarExtra } from '../context/SidebarExtraContext';
import { useCsvActions } from '../context/CsvActionsContext';
import { Resource, Capability, CAPABILITY_DOMAINS, DOMAIN_CAT, RESOURCE_TYPES, RESOURCE_FUNCTIONS } from '../types';
import { Modal } from '../components/common/Modal';
import { parseCsv } from '../utils/csvParser';
import { Hexagon, MousePointerClick, PenLine, Link } from 'lucide-react';
import { useT } from '../i18n';

interface LinkedResource extends Resource {
  contributors?: { id: number; name: string; initials: string }[];
}

// Normalize function names for matching (handle slight variations)
function normalizeFunction(fn: string): string {
  return fn.toLowerCase()
    .replace(/&/g, 'and')
    .replace(/,/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Match resource function to canonical function
function matchFunction(resourceFn: string): string {
  const normalized = normalizeFunction(resourceFn);
  for (const fn of RESOURCE_FUNCTIONS) {
    if (normalizeFunction(fn) === normalized) {
      return fn;
    }
  }
  // Partial matches
  if (normalized.includes('placement')) return 'Relationship & Placement Mgmt';
  if (normalized.includes('it') || normalized.includes('digital')) return 'IT, Digital & Infrastructure';
  return resourceFn; // Return original if no match
}

// Info Tooltip Component
function InfoTooltip({
  resource,
  position,
  onClose
}: {
  resource: Resource;
  position: { x: number; y: number };
  onClose: () => void;
}) {
  const { t } = useT();
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Adjust position to keep tooltip in viewport
  const style: React.CSSProperties = {
    left: Math.min(position.x, window.innerWidth - 320),
    top: Math.min(position.y, window.innerHeight - 200),
  };

  return (
    <>
      <div className="info-tooltip-overlay" onClick={onClose} />
      <div className="info-tooltip" ref={tooltipRef} style={style}>
        <div className="info-tooltip-title">{resource.title}</div>
        <div className="info-tooltip-description">
          {resource.description || t('link.noDescription')}
        </div>
      </div>
    </>
  );
}

// Resource Card Component
function ResourceCard({
  resource,
  isSelected,
  onToggle,
  onShowInfo,
  onEditNotes,
  hasNotes,
  disabled,
  isAggregateView,
  contributors,
}: {
  resource: Resource;
  isSelected: boolean;
  onToggle: () => void;
  onShowInfo: (e: React.MouseEvent) => void;
  onEditNotes: () => void;
  hasNotes: boolean;
  disabled: boolean;
  isAggregateView?: boolean;
  contributors?: { id: number; name: string; initials: string }[];
}) {
  const { t } = useT();
  const selectedClass = isSelected ? `selected selected-${resource.type.toLowerCase()}` : '';
  const isClickable = !disabled && !isAggregateView;

  // Build unique contributor initials string (pipe-separated)
  const contributorInitials = contributors && contributors.length > 0
    ? [...new Set(contributors.map(c => c.initials))].join(' | ')
    : null;

  return (
    <div
      className={`resource-card ${selectedClass} ${isAggregateView ? 'aggregate-view' : ''}`}
      onClick={isClickable ? onToggle : undefined}
      style={{ cursor: isClickable ? 'pointer' : isAggregateView ? 'default' : 'not-allowed', opacity: disabled && !isAggregateView ? 0.7 : 1 }}
    >
      <div className="resource-card-title">{resource.title}</div>
      {contributorInitials && (
        <div className="resource-card-contributors">{contributorInitials}</div>
      )}
      {!isAggregateView && (
        <button
          className={`resource-card-notes ${hasNotes ? 'has-notes' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onEditNotes();
          }}
          title={hasNotes ? t('link.editRefinementNotes') : t('link.addRefinementNotes')}
        >
          <PenLine className="icon-sm" />
        </button>
      )}
      <button
        className="resource-card-info"
        onClick={(e) => {
          e.stopPropagation();
          onShowInfo(e);
        }}
        title={t('link.showDescription')}
      >
        i
      </button>
    </div>
  );
}

// Sidebar Capability List Component
function CapabilityList({
  capabilities,
  selectedCapability,
  onSelect,
  capabilityLinkCounts,
  completedCapabilities,
}: {
  capabilities: Capability[];
  selectedCapability: Capability | null;
  onSelect: (cap: Capability) => void;
  capabilityLinkCounts: Record<number, number>;
  completedCapabilities: Set<number>;
}) {
  const { t } = useT();
  const groupedCapabilities = CAPABILITY_DOMAINS.reduce((acc, domain) => {
    acc[domain] = capabilities.filter((c) => c.domain === domain);
    return acc;
  }, {} as Record<string, Capability[]>);

  return (
    <>
      <div className="cap-list-header" style={{ padding: 'var(--s4) var(--s12)', fontSize: 'var(--t-12)', fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>
        {t('link.capabilities')}
      </div>
      {CAPABILITY_DOMAINS.map((domain) => {
        const domainCapabilities = groupedCapabilities[domain];
        if (domainCapabilities.length === 0) return null;
        return (
          <div key={domain} className="cap-domain">
            <div className="cap-domain-label">{domain}</div>
            {domainCapabilities.map((cap) => {
              const isCompleted = completedCapabilities.has(cap.id);
              const count = capabilityLinkCounts[cap.id] || 0;
              const dotClass = isCompleted ? 'done' : count > 0 ? 'pending' : '';
              return (
                <div
                  key={cap.id}
                  className={`cap-item ${selectedCapability?.id === cap.id ? 'selected' : ''}`}
                  onClick={() => onSelect(cap)}
                >
                  <div className={`cap-item-dot ${dotClass}`} />
                  <div className="cap-item-title">{cap.title}</div>
                  <div className="cap-item-count">{count}</div>
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}


export function LinkingPage() {
  const { t } = useT();
  const { isLinkingAggregateEnabled } = useToggles();
  const { setSidebarExtra } = useSidebarExtra();
  const { setCsvActions } = useCsvActions();

  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedCapability, setSelectedCapability] = useState<Capability | null>(null);
  const [linkedResourceIds, setLinkedResourceIds] = useState<Set<number>>(new Set());
  const [aggregateResources, setAggregateResources] = useState<LinkedResource[]>([]);
  const [showAggregate, setShowAggregate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [showAddResource, setShowAddResource] = useState(false);
  const [newResourceForm, setNewResourceForm] = useState({
    title: '',
    description: '',
    type: 'Human' as Resource['type'],
    function: '',
  });

  // Tooltip state
  const [tooltipResource, setTooltipResource] = useState<Resource | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Refinement notes state
  const [notesResource, setNotesResource] = useState<Resource | null>(null);
  const [notesText, setNotesText] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [resourceNotes, setResourceNotes] = useState<Record<string, string>>({});

  // CSV import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [pendingCsvRows, setPendingCsvRows] = useState<Record<string, string>[]>([]);

  // Track which capabilities have linked resources
  const [capabilityLinkCounts, setCapabilityLinkCounts] = useState<Record<number, number>>({});

  // Track completed capabilities (stored in localStorage)
  const [completedCapabilities, setCompletedCapabilities] = useState<Set<number>>(() => {
    const stored = localStorage.getItem('completedCapabilities');
    if (stored) {
      try {
        return new Set(JSON.parse(stored));
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  useEffect(() => {
    localStorage.setItem('completedCapabilities', JSON.stringify([...completedCapabilities]));
  }, [completedCapabilities]);

  useEffect(() => {
    Promise.all([
      api.getCapabilities().then((data: any) => setCapabilities(data)),
      api.getResources().then((data: any) => setResources(data)),
    ]).finally(() => setLoading(false));
  }, []);

  // Auto-select first capability (in sidebar domain order)
  useEffect(() => {
    if (capabilities.length > 0 && !selectedCapability) {
      for (const domain of CAPABILITY_DOMAINS) {
        const first = capabilities.find(c => c.domain === domain);
        if (first) { setSelectedCapability(first); break; }
      }
    }
  }, [capabilities]);

  // Load link counts for all capabilities
  useEffect(() => {
    if (capabilities.length > 0) {
      Promise.all(
        capabilities.map(async (cap) => {
          const links = await api.getLinks(cap.id) as any[];
          return { id: cap.id, count: links.length };
        })
      ).then((results) => {
        const counts: Record<number, number> = {};
        results.forEach(({ id, count }) => {
          counts[id] = count;
        });
        setCapabilityLinkCounts(counts);
      });
    }
  }, [capabilities]);

  // Register sidebar extra content
  useEffect(() => {
    if (capabilities.length > 0) {
      setSidebarExtra(
        <CapabilityList
          capabilities={capabilities}
          selectedCapability={selectedCapability}
          onSelect={setSelectedCapability}
          capabilityLinkCounts={capabilityLinkCounts}
          completedCapabilities={completedCapabilities}
        />
      );
    }
    return () => setSidebarExtra(null);
  }, [capabilities, selectedCapability, capabilityLinkCounts, completedCapabilities, setSidebarExtra]);

  // Register CSV actions in topbar
  useEffect(() => {
    setCsvActions({
      uploadLabel: t('csv.uploadCsv'),
      onUpload: () => fileInputRef.current?.click(),
      downloadUrl: '/api/links/export-csv',
      downloadLabel: t('csv.exportCsv'),
      uploading: importing,
    });
    return () => setCsvActions(null);
  }, [importing, setCsvActions, t]);

  useEffect(() => {
    if (selectedCapability) {
      loadLinks();
    }
  }, [selectedCapability]);

  const loadLinks = async () => {
    if (!selectedCapability) return;

    try {
      const links = await api.getLinks(selectedCapability.id) as any[];
      setLinkedResourceIds(new Set(links.map((l: any) => l.resource_id)));

      if (showAggregate && isLinkingAggregateEnabled()) {
        const aggregate = await api.getAggregateLinks(selectedCapability.id) as LinkedResource[];
        setAggregateResources(aggregate);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (showAggregate && selectedCapability && isLinkingAggregateEnabled()) {
      api.getAggregateLinks(selectedCapability.id)
        .then((data: any) => setAggregateResources(data))
        .catch(console.error);
    }
  }, [showAggregate, selectedCapability]);

  const handleToggleResource = async (resourceId: number) => {
    if (!selectedCapability) return;

    setSaving(true);
    const newLinked = new Set(linkedResourceIds);

    try {
      if (newLinked.has(resourceId)) {
        const links = await api.getLinks(selectedCapability.id) as any[];
        const link = links.find((l: any) => l.resource_id === resourceId);
        if (link) {
          await api.deleteLink(link.id);
          newLinked.delete(resourceId);
        }
      } else {
        await api.createLink(resourceId, selectedCapability.id);
        newLinked.add(resourceId);
      }
      setLinkedResourceIds(newLinked);
      // Update link count for this capability
      setCapabilityLinkCounts(prev => ({
        ...prev,
        [selectedCapability.id]: newLinked.size
      }));
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleCapabilityCompleted = (capabilityId: number) => {
    setCompletedCapabilities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(capabilityId)) {
        newSet.delete(capabilityId);
      } else {
        newSet.add(capabilityId);
      }
      return newSet;
    });
  };

  const handleAddResource = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const resource = await api.createResource(newResourceForm) as Resource;
      setResources([...resources, resource]);
      setShowAddResource(false);
      setNewResourceForm({ title: '', description: '', type: 'Human', function: '' });

      if (selectedCapability) {
        await api.createLink(resource.id, selectedCapability.id);
        setLinkedResourceIds(new Set([...linkedResourceIds, resource.id]));
      }
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleShowInfo = (resource: Resource, e: React.MouseEvent) => {
    setTooltipResource(resource);
    setTooltipPosition({ x: e.clientX + 10, y: e.clientY + 10 });
  };

  // Load refinement notes for all resources when capability changes
  useEffect(() => {
    if (!selectedCapability) return;
    const loadNotes = async () => {
      const notesMap: Record<string, string> = {};
      await Promise.all(
        resources.map(async (r) => {
          try {
            const ref = await api.getUserRefinement(r.id, selectedCapability.id) as any;
            if (ref?.text) notesMap[r.id] = ref.text;
          } catch { /* no note yet */ }
        })
      );
      setResourceNotes(notesMap);
    };
    loadNotes();
  }, [selectedCapability, resources]);

  const handleOpenNotes = (resource: Resource) => {
    setNotesResource(resource);
    setNotesText(resourceNotes[resource.id] || '');
  };

  const handleSaveNotes = async () => {
    if (!notesResource || !selectedCapability) return;
    setNotesSaving(true);
    try {
      await api.setRefinement(notesResource.id, selectedCapability.id, notesText);
      setResourceNotes((prev) => ({ ...prev, [notesResource.id]: notesText }));
      setNotesResource(null);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setNotesSaving(false);
    }
  };

  // CSV import handler
  const handleCsvFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const rows = parseCsv(text);
      if (rows.length === 0) {
        alert(t('link.csvEmpty'));
        return;
      }
      // Check if user has existing links
      const totalLinks = Object.values(capabilityLinkCounts).reduce((a, b) => a + b, 0);
      if (totalLinks > 0) {
        setPendingCsvRows(rows);
        setShowImportConfirm(true);
      } else {
        executeImport(rows);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const executeImport = async (rows: Record<string, string>[]) => {
    setImporting(true);
    setShowImportConfirm(false);
    try {
      const result = await api.importLinksCsv(rows) as { imported: number; skipped: number };
      alert(t('link.importComplete').replace('{imported}', String(result.imported)).replace('{skipped}', String(result.skipped)));
      // Reload data
      const caps = await api.getCapabilities() as Capability[];
      setCapabilities(caps);
      // Reload link counts
      const counts: Record<number, number> = {};
      await Promise.all(
        caps.map(async (cap) => {
          const links = await api.getLinks(cap.id) as any[];
          counts[cap.id] = links.length;
        })
      );
      setCapabilityLinkCounts(counts);
      // Reload current capability links
      if (selectedCapability) {
        const links = await api.getLinks(selectedCapability.id) as any[];
        setLinkedResourceIds(new Set(links.map((l: any) => l.resource_id)));
      }
    } catch (err: any) {
      alert(t('link.importFailed').replace('{msg}', err.message));
    } finally {
      setImporting(false);
      setPendingCsvRows([]);
    }
  };

  // Filter resources based on search and type filter
  const filteredResources = useMemo(() => {
    if (!searchQuery.trim()) return resources;
    const query = searchQuery.toLowerCase();
    return resources.filter((r) =>
      r.title.toLowerCase().includes(query) ||
      r.description?.toLowerCase().includes(query) ||
      r.function.toLowerCase().includes(query)
    );
  }, [resources, searchQuery]);

  // Organize resources into grid structure
  const resourceGrid = useMemo(() => {
    const grid: Record<string, Record<string, Resource[]>> = {};

    // Initialize grid
    for (const fn of RESOURCE_FUNCTIONS) {
      grid[fn] = {};
      for (const type of RESOURCE_TYPES) {
        grid[fn][type] = [];
      }
    }

    // Populate grid
    const resourcesToShow = showAggregate && isLinkingAggregateEnabled() ? aggregateResources : filteredResources;
    for (const resource of resourcesToShow) {
      const matchedFn = matchFunction(resource.function);
      if (grid[matchedFn]) {
        grid[matchedFn][resource.type]?.push(resource);
      }
    }

    return grid;
  }, [filteredResources, aggregateResources, showAggregate, isLinkingAggregateEnabled]);

  // Check if any row has resources
  const hasResourcesInRow = (fn: string): boolean => {
    return RESOURCE_TYPES.some(type => resourceGrid[fn]?.[type]?.length > 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 200 }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="linking-page">
      {/* Hidden file input for CSV import (always in DOM for topbar button) */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleCsvFileSelect}
      />
      {capabilities.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-icon"><Hexagon /></div>
            <div className="empty-state-title">{t('link.noCapabilitiesTitle')}</div>
            <div className="empty-state-desc">{t('link.noCapabilitiesDesc')}</div>
          </div>
        </div>
      ) : !selectedCapability ? (
        <div className="empty-state">
          <div className="empty-state-icon"><MousePointerClick /></div>
          <div className="empty-state-desc">{t('link.selectCapability')}</div>
        </div>
      ) : (
        <>
          {/* Capability Detail Card */}
          <div className="detail-card">
            <div className="detail-card-header">
              <h4 className="detail-card-title">{selectedCapability.title}</h4>
              <div className="detail-card-header-tags">
                <span className={`meta-tag meta-tag--cat-${DOMAIN_CAT[selectedCapability.domain] || 1}`}>{selectedCapability.domain}</span>
                <span className="meta-tag">{selectedCapability.nature}</span>
              </div>
            </div>
            <div className="detail-card-body">
              <p className="detail-card-description" style={{ maxWidth: Math.min(900, Math.max(300, Math.ceil((selectedCapability.description || '').length / 3 * 7))) }}>
                {selectedCapability.description || t('link.noDescription')}
              </p>
              <span className="detail-card-meta">
                {(linkedResourceIds.size !== 1 ? t('link.resourcesLinked') : t('link.resourceLinked')).replace('{n}', String(linkedResourceIds.size))}
              </span>
            </div>
            <div className="detail-card-footer">
              <label className="toggle toggle-completed">
                <input
                  type="checkbox"
                  className="toggle-input"
                  checked={completedCapabilities.has(selectedCapability.id)}
                  onChange={() => toggleCapabilityCompleted(selectedCapability.id)}
                />
                <span className="toggle-slider toggle-slider-ok"></span>
                <span className="toggle-label-dark">{t('link.completed')}</span>
              </label>
              {isLinkingAggregateEnabled() && (
                <label className="toggle toggle-compact">
                  <input
                    type="checkbox"
                    className="toggle-input"
                    checked={showAggregate}
                    onChange={(e) => setShowAggregate(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-label-dark">{t('link.aggregate')}</span>
                </label>
              )}
              <div className="footer-spacer" />
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('link.filterResources')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {!showAggregate && (
                <button className="btn btn-sm btn-accent" onClick={() => setShowAddResource(true)}>
                  + {t('common.add')}
                </button>
              )}
            </div>
          </div>

          {/* Resource Grid */}
          <div className="matrix-grid-container">
            <div
              className="matrix-grid"
              style={{ '--matrix-cols': '48px repeat(4, minmax(190px, 1fr))' } as React.CSSProperties}
            >
              {/* Header Row */}
              <div className="matrix-grid-corner"></div>
              {RESOURCE_TYPES.map((type) => (
                <div key={type} className="matrix-grid-col">
                  {type}
                </div>
              ))}

              {/* Data Rows */}
              {RESOURCE_FUNCTIONS.map((fn) => {
                if (!hasResourcesInRow(fn) && searchQuery) return null;

                return (
                  <React.Fragment key={fn}>
                    <div className="matrix-grid-row">{fn}</div>
                    {RESOURCE_TYPES.map((type) => (
                      <div key={`${fn}-${type}`} className="matrix-grid-cell matrix-grid-cell--wrap">
                        {resourceGrid[fn]?.[type]?.map((resource) => {
                          const linkedResource = resource as LinkedResource;
                          return (
                            <ResourceCard
                              key={resource.id}
                              resource={resource}
                              isSelected={linkedResourceIds.has(resource.id)}
                              onToggle={() => handleToggleResource(resource.id)}
                              onShowInfo={(e) => handleShowInfo(resource, e)}
                              onEditNotes={() => handleOpenNotes(resource)}
                              hasNotes={!!resourceNotes[resource.id]}
                              disabled={saving}
                              isAggregateView={showAggregate && isLinkingAggregateEnabled()}
                              contributors={linkedResource.contributors}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Aggregate contributors display */}
          {showAggregate && isLinkingAggregateEnabled() && aggregateResources.length > 0 && (
            <div className="mt-12 text-sm text-muted">
              <strong>{t('link.contributorsLabel')}</strong> {t('link.contributorsHelp')}
            </div>
          )}
        </>
      )}

      {/* Info Tooltip */}
      {tooltipResource && (
        <InfoTooltip
          resource={tooltipResource}
          position={tooltipPosition}
          onClose={() => setTooltipResource(null)}
        />
      )}

      {/* Add Resource Modal */}
      {showAddResource && (
        <Modal title={t('link.addResourceTitle')} onClose={() => setShowAddResource(false)}>
          <form onSubmit={handleAddResource}>
            <div className="form-group">
              <label className="form-label">{t('link.labelTitle')}</label>
              <input
                type="text"
                className="form-input"
                value={newResourceForm.title}
                onChange={(e) => setNewResourceForm({ ...newResourceForm, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('link.labelType')}</label>
              <select
                className="form-select"
                value={newResourceForm.type}
                onChange={(e) => setNewResourceForm({ ...newResourceForm, type: e.target.value as Resource['type'] })}
                required
              >
                {RESOURCE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('link.labelFunction')}</label>
              <select
                className="form-select"
                value={newResourceForm.function}
                onChange={(e) => setNewResourceForm({ ...newResourceForm, function: e.target.value })}
                required
              >
                <option value="">{t('link.selectFunction')}</option>
                {RESOURCE_FUNCTIONS.map((fn) => (
                  <option key={fn} value={fn}>
                    {fn}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('link.labelDescription')}</label>
              <textarea
                className="form-textarea"
                value={newResourceForm.description}
                onChange={(e) => setNewResourceForm({ ...newResourceForm, description: e.target.value })}
              />
            </div>
            <div className="modal-footer" style={{ padding: 0, border: 'none', marginTop: 'var(--s16)' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setShowAddResource(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-accent">
                {t('link.createAndLink')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Refinement Notes Editor */}
      {notesResource && (
        <Modal title={t('link.refinementNotesTitle').replace('{title}', notesResource.title)} onClose={() => setNotesResource(null)}>
          <div className="refinement-editor">
            <textarea
              className="rating-textarea refinement-editor-textarea"
              placeholder={t('link.notesPlaceholder')}
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              autoFocus
            />
            <div className="modal-footer" style={{ padding: 0, border: 'none', marginTop: 'var(--s12)' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setNotesResource(null)}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-accent"
                onClick={handleSaveNotes}
                disabled={notesSaving}
              >
                {notesSaving ? t('link.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* CSV Import Confirmation */}
      {showImportConfirm && (
        <Modal title={t('link.overwriteTitle')} onClose={() => { setShowImportConfirm(false); setPendingCsvRows([]); }}>
          <p className="text-sm" style={{ lineHeight: 1.6, marginBottom: 'var(--s16)' }}>
            {t('link.overwriteBody1')}<strong>{t('link.overwriteBodyStrong')}</strong>{t('link.overwriteBody2').replace('{n}', String(pendingCsvRows.length))}
          </p>
          <p className="text-sm" style={{ lineHeight: 1.6, marginBottom: 'var(--s16)', color: 'var(--ink-3)' }}>
            {t('link.overwriteWarning')}
          </p>
          <div className="modal-footer" style={{ padding: 0, border: 'none', marginTop: 'var(--s16)' }}>
            <button type="button" className="btn btn-ghost" onClick={() => { setShowImportConfirm(false); setPendingCsvRows([]); }}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-accent"
              onClick={() => executeImport(pendingCsvRows)}
            >
              {t('link.overwriteImport')}
            </button>
          </div>
        </Modal>
      )}

    </div>
  );
}
